import React, { useState, useEffect, ChangeEvent, MouseEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { Menu, MenuItem, Divider } from "@mui/material";
import {
  Link2,
  Edit,
  Brain,
  FileText,
  PlusCircle,
  Mail,
  MapPin,
  User,
  ChevronDown,
  Save,
  X,
  Trash2,
  AlertTriangle,
  Shield,
  Clock,
} from "lucide-react";
import api from "../services/api";
import { recruitingAPI } from "../services/recruitingApi";
import CandidateForm from "../components/CandidateForm";
import ApplicationForm from "../components/ApplicationForm";
import CVGeneration from "../components/CVGeneration";
import CandidateAssessment from "../components/CandidateAssessment";
import TechCheckForm from "../components/TechCheckForm";
import EmailComposer from "../components/EmailComposer";
import JobSpecificCVGeneration from "../components/JobSpecificCVGeneration";
import ChangeHistory from "../components/ChangeHistory";
import { CandidateStatusBadgeWithActions } from "../components/CandidateStatusControl";
import CandidateGdprSection from "../components/CandidateGdprSection";
import InlineEditableField from "../components/InlineEditableField";
import ConfirmationModal from "../components/ConfirmationModal";
import AppCountDropdown from "../components/AppCountDropdown";
import { APPLICATION_STATUS_ORDER } from "../components/StatusControl/configs/applicationConfig";
import {
  SkillsTab,
  AssessmentTab,
  ApplicationsTab,
  NotesTab,
  SummaryTab,
} from "./CandidateDetail/tabs";
import { useNotes } from "../hooks/useNotes";
import { useTechCheckerAssignment } from "../hooks/candidate/useTechCheckerAssignment";
import { useRecruiterAssignment } from "../hooks/candidate/useRecruiterAssignment";
import CandidateKamAssignment from "../components/CandidateKamAssignment";
import PersonnelNotifications from "../components/PersonnelNotifications";
import { useSkillsAssessment } from "../hooks/candidate/useSkillsAssessment";
import {
  useCandidateData,
  type Candidate,
  type Application,
  type Job,
} from "../hooks/candidate/useCandidateData";
import {
  useInlineEdit,
  required,
  email as emailRule,
  url,
  numeric,
} from "../hooks/useInlineEdit";
import { getCurrentUser } from "../utils/auth";
import "./CandidateDetail.css";

interface CurrentUser {
  id: number;
  role: string;
}

// Candidate, Application, Job interfaces imported from useCandidateData hook
// Note interface imported from useNotes hook
// TechChecker interfaces imported from useTechCheckerAssignment hook
// Skills types imported from useSkillsAssessment hook

type TabType =
  | "summary"
  | "applications"
  | "notes"
  | "skills"
  | "assessment"
  | "tech-check"
  | "history"
  | "gdpr";

const CandidateDetail: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Get user role from auth utility
  const user = (getCurrentUser() || {}) as CurrentUser;
  const isTechChecker = user.role === "tech_checker";

  // Check for tab parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = (urlParams.get("tab") ||
    (isTechChecker ? "tech-check" : "summary")) as TabType;
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Auto-scroll to applications section when navigating via badge click
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "applications") {
      setTimeout(() => {
        document
          .getElementById("applications-tab-content")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateApplicationModal, setShowCreateApplicationModal] =
    useState(false);
  const [showCVGenerationModal, setShowCVGenerationModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [selectedJobForAssessment, setSelectedJobForAssessment] =
    useState<Job | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedJobForEmail, setSelectedJobForEmail] = useState<number | null>(
    null,
  );
  const [showCVModal, setShowCVModal] = useState(false);
  const [selectedApplicationForCV, setSelectedApplicationForCV] =
    useState<Application | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [moreActionsAnchor, setMoreActionsAnchor] =
    useState<HTMLElement | null>(null);

  // Candidate data - using custom hook
  const {
    candidate,
    candidateLoading,
    candidateError,
    applications,
    applicationsLoading,
    gdprStatusData,
    allJobs,
    jobsForAssessment,
    jobsLoading,
    history,
    historyLoading,
    historyError,
    generateSummary,
    isGeneratingSummary,
  } = useCandidateData({
    candidateId: id,
    enabled: !!id,
  });

  // Notes - using custom hook
  const {
    notes,
    isLoading: notesLoading,
    newNote,
    setNewNote,
    handleAddNote,
    isCreating: isCreatingNote,
  } = useNotes({
    entityType: "candidate",
    entityId: id,
    enabled: !!id,
  });

  // Tech Checker Assignment - using custom hook
  const {
    techCheckers,
    availableTechCheckers,
    selectedTechCheckerId,
    setSelectedTechCheckerId,
    isAssigningTechChecker,
    handleAssignTechChecker,
    handleRemoveTechChecker,
    canManageTechCheckers,
    notification: tcNotification,
    closeNotification: tcCloseNotification,
    confirmDialog: tcConfirmDialog,
    confirmRemoval: tcConfirmRemoval,
    cancelRemoval: tcCancelRemoval,
  } = useTechCheckerAssignment({
    candidateId: id,
    userRole: user.role,
    enabled: !!id,
  });

  const {
    recruiters: assignedRecruiters,
    availableRecruiters,
    selectedRecruiterId,
    setSelectedRecruiterId,
    isAssigningRecruiter,
    handleAssignRecruiter,
    handleRemoveRecruiter,
    canManageRecruiters,
    notification: recNotification,
    closeNotification: recCloseNotification,
    confirmDialog: recConfirmDialog,
    confirmRemoval: recConfirmRemoval,
    cancelRemoval: recCancelRemoval,
  } = useRecruiterAssignment({
    candidateId: id,
    userRole: user.role,
    enabled: !!id,
  });

  // Inline Edit - using custom hook
  const editableFields: (keyof Candidate)[] = [
    "first_name",
    "last_name",
    "email",
    "phone",
    "location",
    "linkedin_url",
    "main_skill",
    "seniority",
    "role_type",
    "expected_rate",
    "notice_period",
    "english_level",
    "source",
    "resume_url",
    "summary",
  ];

  const {
    isEditMode,
    editedEntity: editedCandidate,
    isDirty,
    validationErrors,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleFieldChange,
    handleSaveEdit,
  } = useInlineEdit<Candidate>({
    entity: candidate,
    queryKey: ["candidate", id],
    saveFn: (data) =>
      api.recruiting.updateCandidate(Number.parseInt(id!, 10), data),
    editableFields,
    validationRules: [
      required<Candidate>(
        "first_name",
        t("candidates.detail.validation.firstNameRequired"),
      ),
      required<Candidate>(
        "last_name",
        t("candidates.detail.validation.lastNameRequired"),
      ),
      required<Candidate>(
        "email",
        t("candidates.detail.validation.emailRequired"),
      ),
      emailRule<Candidate>(
        "email",
        t("candidates.detail.validation.emailInvalid"),
      ),
      url<Candidate>(
        "linkedin_url",
        t("candidates.detail.validation.linkedinUrlInvalid"),
      ),
      numeric<Candidate>(
        "expected_rate",
        t("candidates.detail.validation.expectedRateNumeric"),
      ),
    ],
    fieldTransformers: {
      source: (val) => (val as string)?.toLowerCase() || "",
    },
  });

  // Skills Assessment - using custom hook
  const {
    candidateSkills,
    skillsVersion,
    isLoading: skillsLoading,
    selectedRole,
    setSelectedRole,
    roleSkillSets,
    skillsAssessmentType,
    setSkillsAssessmentType,
    technicalSkills,
    isGeneratingTechnicalSkills,
    handleGenerateTechnicalSkills,
    aiRecommendations,
    isGeneratingRecommendations,
    handleGenerateAIRecommendations,
    updateSkills,
  } = useSkillsAssessment({
    candidateId: id,
    candidate,
    enabled: !!id,
  });

  // All inline edit state and handlers are now in useInlineEdit hook

  // Select options for dropdowns
  const mainSkillOptions = [
    { value: "Azure", label: "Azure" },
    { value: "AWS", label: "AWS" },
    { value: "GCP", label: "GCP" },
    { value: "Kubernetes", label: "Kubernetes" },
    { value: "Terraform", label: "Terraform" },
    { value: "Docker", label: "Docker" },
    { value: "Python", label: "Python" },
    { value: "JavaScript", label: "JavaScript" },
    { value: "React", label: "React" },
    { value: "Node.js", label: "Node.js" },
  ];

  const seniorityOptions = [
    { value: "Junior", label: "Junior" },
    { value: "Mid", label: "Mid" },
    { value: "Senior", label: "Senior" },
    { value: "Lead", label: "Lead" },
  ];

  const noticePeriodOptions = [
    {
      value: "Immediate",
      label: t("candidates.detail.noticePeriods.immediate"),
    },
    { value: "1 week", label: t("candidates.detail.noticePeriods.oneWeek") },
    { value: "2 weeks", label: t("candidates.detail.noticePeriods.twoWeeks") },
    { value: "1 month", label: t("candidates.detail.noticePeriods.oneMonth") },
    {
      value: "2 months",
      label: t("candidates.detail.noticePeriods.twoMonths"),
    },
    {
      value: "3 months",
      label: t("candidates.detail.noticePeriods.threeMonths"),
    },
  ];

  const englishLevelOptions = [
    { value: "A1", label: "A1 - Beginner" },
    { value: "A2", label: "A2 - Elementary" },
    { value: "B1", label: "B1 - Intermediate" },
    { value: "B2", label: "B2 - Upper Intermediate" },
    { value: "C1", label: "C1 - Advanced" },
    { value: "C2", label: "C2 - Proficient" },
  ];

  const sourceOptions = [
    { value: "linkedin", label: "LinkedIn" },
    { value: "job-board", label: t("candidates.detail.sources.jobBoard") },
    { value: "referral", label: t("candidates.detail.sources.referral") },
    { value: "recruiter", label: t("candidates.detail.sources.recruiter") },
    {
      value: "direct-application",
      label: t("candidates.detail.sources.directApplication"),
    },
    { value: "other", label: t("candidates.detail.sources.other") },
  ];

  const handleCreateApplication = () => {
    setShowCreateApplicationModal(true);
  };

  const handleGenerateCV = () => {
    setShowCVGenerationModal(true);
  };

  const handleGenerateJobSpecificCV = (application: Application) => {
    setSelectedApplicationForCV(application);
    setShowCVModal(true);
  };

  const handleCloseCVModal = () => {
    setShowCVModal(false);
    setSelectedApplicationForCV(null);
  };

  const handleAssessForRole = (job: Job) => {
    setSelectedJobForAssessment(job);
    setShowAssessmentModal(true);
  };

  const handleBackToList = () => {
    navigate("/candidates");
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await recruitingAPI.deleteCandidate(id!);
      setShowDeleteModal(false);
      navigate("/candidates");
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      alert(
        t("candidates.detail.errorDeletingCandidate") +
          ": " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  if (candidateLoading) {
    return (
      <div className="candidate-detail">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("common:loading.candidateDetails")}</p>
        </div>
      </div>
    );
  }

  if (candidateError || !candidate) {
    return (
      <div className="candidate-detail">
        <div className="error-state">
          <h2>{t("candidates.detail.candidateNotFound")}</h2>
          <p>{t("candidates.detail.candidateNotFoundDescription")}</p>
          <button onClick={handleBackToList} className="btn-primary">
            {t("candidates.detail.backToCandidates")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-detail">
      {/* Header - Three Row Layout */}
      <div className="candidate-header">
        {/* Row 1: Navigation */}
        <div className="header-nav">
          <button onClick={handleBackToList} className="back-btn">
            ← {t("candidates.detail.backToCandidates")}
          </button>
        </div>

        {/* Row 2: Identity + Status Badge + GDPR Indicator */}
        <div className="header-main">
          <div className="candidate-identity">
            <h1>
              {candidate.first_name} {candidate.last_name}
            </h1>
            <p className="candidate-email">{candidate.email}</p>
          </div>
          <div className="header-badges">
            <CandidateStatusBadgeWithActions
              candidate={candidate}
              size="medium"
              disabled={isTechChecker}
              onStatusChange={(updatedCandidate) => {
                queryClient.setQueryData(["candidate", id], {
                  data: updatedCandidate,
                });
              }}
            />
            {/* GDPR Status Indicator with Lifecycle Tooltip */}
            {gdprStatusData &&
              (() => {
                const GDPR_STATUS_CONFIG: Record<
                  string,
                  {
                    label: string;
                    description: string;
                    bg: string;
                    color: string;
                    icon: React.ReactNode;
                  }
                > = {
                  none: {
                    label: t("candidates.gdprStatus.noConsent"),
                    description: t("candidates.gdprStatus.desc.noConsent"),
                    bg: "#f8d7da",
                    color: "#721c24",
                    icon: <AlertTriangle size={14} />,
                  },
                  pending: {
                    label: t("candidates.gdprStatus.pending"),
                    description: t("candidates.gdprStatus.desc.pending"),
                    bg: "#fff3cd",
                    color: "#856404",
                    icon: <Clock size={14} />,
                  },
                  given: {
                    label: t("candidates.gdprStatus.consentGiven"),
                    description: t("candidates.gdprStatus.desc.consentGiven"),
                    bg: "#d4edda",
                    color: "#155724",
                    icon: <Shield size={14} />,
                  },
                  withdrawn: {
                    label: t("candidates.gdprStatus.withdrawn"),
                    description: t("candidates.gdprStatus.desc.withdrawn"),
                    bg: "#f8d7da",
                    color: "#721c24",
                    icon: <AlertTriangle size={14} />,
                  },
                  deleted: {
                    label: t("candidates.gdprStatus.dataDeleted"),
                    description: t("candidates.gdprStatus.desc.dataDeleted"),
                    bg: "#d6d8db",
                    color: "#383d41",
                    icon: <Trash2 size={14} />,
                  },
                  grandfathered: {
                    label: t("candidates.gdprStatus.grandfathered"),
                    description: t("candidates.gdprStatus.desc.grandfathered"),
                    bg: "#dbeafe",
                    color: "#1e40af",
                    icon: <Shield size={14} />,
                  },
                };
                const statusKey = gdprStatusData.isGrandfathered
                  ? "grandfathered"
                  : gdprStatusData.status || "none";
                const config =
                  GDPR_STATUS_CONFIG[statusKey] || GDPR_STATUS_CONFIG.none;
                const allStatuses = [
                  "none",
                  "pending",
                  "given",
                  "withdrawn",
                  "deleted",
                  "grandfathered",
                ];
                return (
                  <div className="gdpr-status-wrapper">
                    <button
                      className="gdpr-status-indicator"
                      onClick={() => setActiveTab("gdpr")}
                      title={config.label}
                      style={{
                        backgroundColor: config.bg,
                        color: config.color,
                        borderColor: config.color,
                      }}
                    >
                      {config.icon}
                      {config.label}
                    </button>
                    <div className="gdpr-lifecycle-tooltip">
                      <div className="gdpr-lifecycle-tooltip-inner">
                        <div className="gdpr-tooltip-title">
                          {t("candidates.gdprStatus.lifecycleTitle")}
                        </div>
                        {allStatuses.map((key) => {
                          const s = GDPR_STATUS_CONFIG[key];
                          const isCurrent = key === statusKey;
                          return (
                            <div
                              key={key}
                              className={`gdpr-tooltip-row${isCurrent ? " gdpr-tooltip-row--current" : ""}`}
                            >
                              <span
                                className="gdpr-tooltip-dot"
                                style={{
                                  backgroundColor: isCurrent
                                    ? s.color
                                    : "transparent",
                                  borderColor: s.color,
                                }}
                              />
                              <div className="gdpr-tooltip-text">
                                <span className="gdpr-tooltip-label">
                                  {s.label}
                                </span>
                                <span className="gdpr-tooltip-desc">
                                  {s.description}
                                </span>
                              </div>
                              {isCurrent && (
                                <span className="gdpr-tooltip-current">
                                  {t("candidates.gdprStatus.current")}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            {/* Application Status Badge */}
            {applications.length > 0 &&
              (() => {
                const activeApps = applications.filter(
                  (a) => a.status !== "rejected" && a.status !== "success",
                );
                const appsToCheck =
                  activeApps.length > 0 ? activeApps : applications;
                const mostAdvanced = appsToCheck.reduce((best, app) => {
                  const bestIdx = APPLICATION_STATUS_ORDER.indexOf(best.status);
                  const appIdx = APPLICATION_STATUS_ORDER.indexOf(app.status);
                  return appIdx > bestIdx ? app : best;
                }, appsToCheck[0]);
                return (
                  <AppCountDropdown
                    candidateId={Number(id)}
                    count={applications.length}
                    mostAdvancedStatus={mostAdvanced.status}
                    applications={applications}
                  />
                );
              })()}
          </div>
        </div>

        {/* Row 3: Actions + Tech Checker */}
        {!isTechChecker && (
          <div className="header-actions-bar">
            <div className="primary-actions">
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSaveEdit}
                    className="btn-primary"
                    disabled={isSaving}
                  >
                    <Save size={16} />{" "}
                    {isSaving
                      ? t("common:loading.saving")
                      : t("common:actions.save")}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                    disabled={isSaving}
                  >
                    <X size={16} /> {t("common:actions.cancel")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleStartEdit}
                    className="btn btn-secondary"
                  >
                    <Edit size={16} /> {t("candidates.detail.editCandidate")}
                  </button>
                  <button
                    onClick={handleCreateApplication}
                    className="btn-primary"
                  >
                    <PlusCircle size={16} />{" "}
                    {t("candidates.detail.createApplication")}
                  </button>
                  <button
                    className="more-actions-btn"
                    onClick={(e: MouseEvent<HTMLButtonElement>) =>
                      setMoreActionsAnchor(e.currentTarget)
                    }
                  >
                    {t("candidates.detail.moreActions")}{" "}
                    <ChevronDown size={14} />
                  </button>
                  <Menu
                    anchorEl={moreActionsAnchor}
                    open={Boolean(moreActionsAnchor)}
                    onClose={() => setMoreActionsAnchor(null)}
                  >
                    {candidate.candidate_code && (
                      <MenuItem
                        onClick={async () => {
                          setMoreActionsAnchor(null);
                          try {
                            const result =
                              await recruitingAPI.generateShareLink(
                                candidate.id,
                              );
                            const shareUrl = result?.url;
                            if (!shareUrl) {
                              console.error(
                                "Share link response missing URL:",
                                result,
                              );
                              alert(
                                "Share link was generated but the URL was empty. Please try again.",
                              );
                              return;
                            }
                            try {
                              await navigator.clipboard.writeText(shareUrl);
                              alert("Share link copied! Valid for 72 hours.");
                            } catch (clipErr) {
                              console.warn("Clipboard write failed:", clipErr);
                              prompt(
                                "Copy this share link (valid for 72 hours):",
                                shareUrl,
                              );
                            }
                          } catch (err) {
                            console.error(
                              "Failed to generate share link:",
                              err,
                            );
                            alert("Failed to generate share link.");
                          }
                        }}
                      >
                        <Link2 size={16} style={{ marginRight: 12 }} />{" "}
                        {t("candidates.detail.publicProfile")}
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        handleGenerateAIRecommendations();
                        setMoreActionsAnchor(null);
                      }}
                      disabled={isGeneratingRecommendations}
                    >
                      <Brain size={16} style={{ marginRight: 12 }} />{" "}
                      {isGeneratingRecommendations
                        ? t("common:loading.generating")
                        : t("candidates.detail.generateSkillsAssessment")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleGenerateCV();
                        setMoreActionsAnchor(null);
                      }}
                    >
                      <FileText size={16} style={{ marginRight: 12 }} />{" "}
                      {t("candidates.detail.generateCV")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setMoreActionsAnchor(null);
                        if (applications && applications.length > 0) {
                          setShowEmailModal(true);
                        } else {
                          alert(
                            t(
                              "candidates.detail.createApplicationFirstForEmail",
                            ),
                          );
                        }
                      }}
                    >
                      <Mail size={16} style={{ marginRight: 12 }} />{" "}
                      {t("candidates.detail.emailCandidate")}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        handleDeleteClick();
                        setMoreActionsAnchor(null);
                      }}
                      sx={{ color: "error.main" }}
                    >
                      <Trash2 size={16} style={{ marginRight: 12 }} />{" "}
                      {t("candidates.detail.deleteCandidate")}
                    </MenuItem>
                  </Menu>
                </>
              )}
            </div>

            {/* KAM Inline */}
            <CandidateKamAssignment candidateId={Number(id)} />

            {/* Recruiter + Tech Checker group */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {/* Recruiter Inline */}
              <div className="tech-checker-inline">
                <span className="tech-checker-label">
                  <User size={14} />{" "}
                  {t("candidates.detail.recruiter", "Recruiter")}:
                </span>
                {assignedRecruiters.length > 0 ? (
                  <div className="tech-checker-badges-inline">
                    {assignedRecruiters.map((r) => (
                      <span key={r.id} className="tech-checker-badge-inline">
                        {r.recruiter_name || t("common:misc.unknown")}
                        {canManageRecruiters && (
                          <button
                            onClick={() =>
                              handleRemoveRecruiter(r.recruiter_id)
                            }
                            className="tech-checker-remove-inline"
                            title={t("common:actions.remove")}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                    {canManageRecruiters && (
                      <select
                        value={selectedRecruiterId}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          setSelectedRecruiterId(e.target.value);
                          if (e.target.value) {
                            handleAssignRecruiter(e.target.value);
                          }
                        }}
                        className="tech-checker-add-select"
                        title={t(
                          "candidates.detail.addRecruiter",
                          "Add Recruiter",
                        )}
                      >
                        <option value="">+ {t("common:actions.add")}</option>
                        {availableRecruiters
                          .filter(
                            (r) =>
                              !assignedRecruiters.some(
                                (ar) => ar.recruiter_id === r.id,
                              ),
                          )
                          .map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.username}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                ) : canManageRecruiters ? (
                  <select
                    value={selectedRecruiterId}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      setSelectedRecruiterId(e.target.value);
                      if (e.target.value) {
                        handleAssignRecruiter(e.target.value);
                      }
                    }}
                    className="tech-checker-add-select"
                    disabled={isAssigningRecruiter}
                  >
                    <option value="">
                      {isAssigningRecruiter
                        ? t("common:loading.assigning")
                        : `+ ${t("common:actions.assign")}`}
                    </option>
                    {availableRecruiters.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.username}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    className="tech-checker-badge-inline"
                    style={{ opacity: 0.6 }}
                  >
                    {t("common:misc.none", "None")}
                  </span>
                )}
              </div>

              {/* Tech Checker Inline */}
              {canManageTechCheckers && (
                <div className="tech-checker-inline">
                  <span className="tech-checker-label">
                    <User size={14} /> {t("candidates.detail.techChecker")}:
                  </span>
                  {techCheckers.length > 0 ? (
                    <div className="tech-checker-badges-inline">
                      {techCheckers.map((tc) => (
                        <span key={tc.id} className="tech-checker-badge-inline">
                          {tc.tech_checker_name || t("common:misc.unknown")}
                          <button
                            onClick={() =>
                              handleRemoveTechChecker(tc.tech_checker_id)
                            }
                            className="tech-checker-remove-inline"
                            title={t("common:actions.remove")}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <select
                        value={selectedTechCheckerId}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          setSelectedTechCheckerId(e.target.value);
                          if (e.target.value) {
                            handleAssignTechChecker(e.target.value);
                          }
                        }}
                        className="tech-checker-add-select"
                        title={t("candidates.detail.addTechChecker")}
                      >
                        <option value="">+ {t("common:actions.add")}</option>
                        {availableTechCheckers
                          .filter(
                            (tc) =>
                              !techCheckers.some(
                                (atc) => atc.tech_checker_id === tc.id,
                              ),
                          )
                          .map((tc) => (
                            <option key={tc.id} value={tc.id}>
                              {tc.username}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : (
                    <select
                      value={selectedTechCheckerId}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        setSelectedTechCheckerId(e.target.value);
                        if (e.target.value) {
                          handleAssignTechChecker(e.target.value);
                        }
                      }}
                      className="tech-checker-add-select"
                      disabled={isAssigningTechChecker}
                    >
                      <option value="">
                        {isAssigningTechChecker
                          ? t("common:loading.assigning")
                          : `+ ${t("common:actions.assign")}`}
                      </option>
                      {availableTechCheckers.map((tc) => (
                        <option key={tc.id} value={tc.id}>
                          {tc.username}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Validation Errors Banner */}
      {validationErrors.general && (
        <div className="validation-error-banner">
          {validationErrors.general}
        </div>
      )}

      {/* Candidate Info Cards */}
      <div className={`candidate-info-cards ${isEditMode ? "edit-mode" : ""}`}>
        <div className="info-card">
          <h3>{t("candidates.detail.contactInformation")}</h3>
          <div className="info-grid">
            {isEditMode && (
              <>
                <InlineEditableField
                  label={t("candidates.detail.fields.firstName")}
                  value={editedCandidate.first_name}
                  type="text"
                  onChange={(val) => handleFieldChange("first_name", val)}
                  isEditing={true}
                  error={validationErrors.first_name}
                  required
                />
                <InlineEditableField
                  label={t("candidates.detail.fields.lastName")}
                  value={editedCandidate.last_name}
                  type="text"
                  onChange={(val) => handleFieldChange("last_name", val)}
                  isEditing={true}
                  error={validationErrors.last_name}
                  required
                />
              </>
            )}
            <InlineEditableField
              label={t("candidates.detail.fields.email")}
              value={isEditMode ? editedCandidate.email : candidate.email}
              type="email"
              onChange={(val) => handleFieldChange("email", val)}
              isEditing={isEditMode}
              error={validationErrors.email}
              required
            />
            <InlineEditableField
              label={t("candidates.detail.fields.phone")}
              value={isEditMode ? editedCandidate.phone : candidate.phone}
              type="tel"
              onChange={(val) => handleFieldChange("phone", val)}
              isEditing={isEditMode}
            />
            <InlineEditableField
              label={t("candidates.detail.fields.location")}
              value={isEditMode ? editedCandidate.location : candidate.location}
              type="text"
              onChange={(val) => handleFieldChange("location", val)}
              isEditing={isEditMode}
              placeholder="e.g., Remote, Baku, London"
            />
            <InlineEditableField
              label={t("candidates.detail.fields.linkedin")}
              value={
                isEditMode
                  ? editedCandidate.linkedin_url
                  : candidate.linkedin_url
              }
              type="url"
              onChange={(val) => handleFieldChange("linkedin_url", val)}
              isEditing={isEditMode}
              error={validationErrors.linkedin_url}
              placeholder="https://linkedin.com/in/username"
            >
              {!isEditMode && candidate.linkedin_url && (
                <a
                  href={candidate.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("candidates.detail.viewProfile")}
                </a>
              )}
            </InlineEditableField>
          </div>
        </div>

        <div className="info-card">
          <h3>{t("candidates.detail.professionalDetails")}</h3>
          <div className="info-grid">
            <InlineEditableField
              label={t("candidates.detail.fields.mainSkill")}
              value={
                isEditMode ? editedCandidate.main_skill : candidate.main_skill
              }
              type="select"
              options={mainSkillOptions}
              onChange={(val) => handleFieldChange("main_skill", val)}
              isEditing={isEditMode}
            />
            <InlineEditableField
              label={t("candidates.detail.fields.seniority")}
              value={
                isEditMode ? editedCandidate.seniority : candidate.seniority
              }
              type="select"
              options={seniorityOptions}
              onChange={(val) => handleFieldChange("seniority", val)}
              isEditing={isEditMode}
            />
            <InlineEditableField
              label={t("candidates.detail.fields.roleType")}
              value={
                isEditMode ? editedCandidate.role_type : candidate.role_type
              }
              type="text"
              onChange={(val) => handleFieldChange("role_type", val)}
              isEditing={isEditMode}
              placeholder="e.g., DevOps Engineer, Full Stack Developer"
            />
            {!isTechChecker && (
              <InlineEditableField
                label={t("candidates.detail.fields.expectedRate")}
                value={
                  isEditMode
                    ? editedCandidate.expected_rate
                    : candidate.expected_rate
                }
                type="number"
                onChange={(val) => handleFieldChange("expected_rate", val)}
                isEditing={isEditMode}
                error={validationErrors.expected_rate}
                displayValue={
                  candidate.expected_rate
                    ? `$${candidate.expected_rate}/day`
                    : undefined
                }
              />
            )}
            <InlineEditableField
              label={t("candidates.detail.fields.noticePeriod")}
              value={
                isEditMode
                  ? editedCandidate.notice_period
                  : candidate.notice_period
              }
              type="select"
              options={noticePeriodOptions}
              onChange={(val) => handleFieldChange("notice_period", val)}
              isEditing={isEditMode}
            />
            <InlineEditableField
              label={t("candidates.detail.fields.englishLevel")}
              value={
                isEditMode
                  ? editedCandidate.english_level
                  : candidate.english_level
              }
              type="select"
              options={englishLevelOptions}
              onChange={(val) => handleFieldChange("english_level", val)}
              isEditing={isEditMode}
            />
          </div>
        </div>

        <div className="info-card">
          <h3>{t("candidates.detail.statusAndSource")}</h3>
          <div className="info-grid">
            <InlineEditableField
              label={t("candidates.detail.fields.source")}
              value={isEditMode ? editedCandidate.source : candidate.source}
              type="select"
              options={sourceOptions}
              onChange={(val) => handleFieldChange("source", val)}
              isEditing={isEditMode}
            />
            <div className="info-item">
              <span className="label">{t("candidates.detail.created")}:</span>
              <span className="value">
                {new Date(candidate.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="info-item">
              <span className="label">{t("candidates.detail.updated")}:</span>
              <span className="value">
                {new Date(candidate.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      {candidate.tags && candidate.tags.length > 0 && (
        <div className="candidate-tags-section">
          <h3>{t("candidates.detail.tags")}</h3>
          <div className="tags-container">
            {candidate.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="candidate-tabs">
        <div className="tab-nav">
          {!isTechChecker && (
            <>
              <button
                className={`tab-btn ${activeTab === "summary" ? "active" : ""}`}
                onClick={() => setActiveTab("summary")}
              >
                {t("candidates.detail.tabs.summary")}
              </button>
              <button
                className={`tab-btn ${activeTab === "applications" ? "active" : ""}`}
                onClick={() => setActiveTab("applications")}
              >
                {t("candidates.detail.tabs.applications")} (
                {applications.length})
              </button>
              <button
                className={`tab-btn ${activeTab === "notes" ? "active" : ""}`}
                onClick={() => setActiveTab("notes")}
              >
                {t("candidates.detail.tabs.notes")} ({notes.length})
              </button>
              <button
                className={`tab-btn ${activeTab === "skills" ? "active" : ""}`}
                onClick={() => setActiveTab("skills")}
              >
                {t("candidates.detail.tabs.skillsAssessment")}
              </button>
              <button
                className={`tab-btn ${activeTab === "assessment" ? "active" : ""}`}
                onClick={() => setActiveTab("assessment")}
              >
                {t("candidates.detail.tabs.roleAssessment")}
              </button>
              <button
                className={`tab-btn ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                {t("candidates.detail.tabs.history")}
              </button>
              <button
                className={`tab-btn ${activeTab === "gdpr" ? "active" : ""}`}
                onClick={() => setActiveTab("gdpr")}
              >
                {t("candidates.detail.tabs.gdpr")}
              </button>
            </>
          )}
          <button
            className={`tab-btn ${activeTab === "tech-check" ? "active" : ""}`}
            onClick={() => setActiveTab("tech-check")}
          >
            {t("candidates.detail.tabs.techCheck")}
          </button>
        </div>

        <div className="tab-content">
          {activeTab === "summary" && !isTechChecker && (
            <SummaryTab
              summary={candidate.summary}
              resumeUrl={candidate.resume_url}
              isGeneratingSummary={isGeneratingSummary}
              onGenerateSummary={generateSummary}
            />
          )}

          {activeTab === "applications" && !isTechChecker && (
            <div id="applications-tab-content">
              <ApplicationsTab
                applications={applications}
                applicationsLoading={applicationsLoading}
                onCreateApplication={handleCreateApplication}
                onGenerateJobSpecificCV={handleGenerateJobSpecificCV}
              />
            </div>
          )}

          {activeTab === "notes" && !isTechChecker && (
            <NotesTab
              notes={notes}
              notesLoading={notesLoading}
              newNote={newNote}
              setNewNote={setNewNote}
              onAddNote={handleAddNote}
              isCreatingNote={isCreatingNote}
            />
          )}

          {activeTab === "skills" && !isTechChecker && (
            <SkillsTab
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              skillsLoading={skillsLoading}
              skillsAssessmentType={skillsAssessmentType}
              setSkillsAssessmentType={setSkillsAssessmentType}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              roleSkillSets={roleSkillSets}
              handleGenerateTechnicalSkills={handleGenerateTechnicalSkills}
              isGeneratingTechnicalSkills={isGeneratingTechnicalSkills}
              technicalSkills={technicalSkills}
              candidateSkills={candidateSkills}
              skillsVersion={skillsVersion}
              updateSkills={updateSkills}
              aiRecommendations={aiRecommendations}
              isGeneratingRecommendations={isGeneratingRecommendations}
              handleGenerateAIRecommendations={handleGenerateAIRecommendations}
            />
          )}

          {activeTab === "assessment" && !isTechChecker && (
            <AssessmentTab
              jobsLoading={jobsLoading}
              jobsForAssessment={jobsForAssessment}
              onAssessForRole={handleAssessForRole}
            />
          )}

          {activeTab === "tech-check" && (
            <div className="tech-check-tab">
              {candidate && (
                <TechCheckForm
                  candidateId={Number.parseInt(id!)}
                  candidate={candidate}
                  jobs={allJobs}
                />
              )}
            </div>
          )}

          {activeTab === "history" && !isTechChecker && (
            <div className="history-tab">
              <div className="history-header">
                <h3>{t("candidates.detail.changeHistory")}</h3>
                <p style={{ color: "#6c757d", marginBottom: "16px" }}>
                  {t("candidates.detail.changeHistoryDescription")}
                </p>
              </div>
              <ChangeHistory
                history={history}
                isLoading={historyLoading}
                error={historyError as Error | null}
              />
            </div>
          )}

          {activeTab === "gdpr" && !isTechChecker && (
            <div className="gdpr-tab">
              <div className="gdpr-header">
                <h3>{t("candidates.detail.gdprConsentManagement")}</h3>
                <p style={{ color: "#6c757d", marginBottom: "16px" }}>
                  {t("candidates.detail.gdprConsentDescription")}
                </p>
              </div>
              <CandidateGdprSection
                candidateId={Number.parseInt(id!, 10)}
                candidateFirstName={candidate.first_name}
                userRole={user.role}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <CandidateForm
              candidate={candidate}
              onSave={() => {
                setShowEditModal(false);
                queryClient.invalidateQueries(["candidate", id]);
              }}
              onCancel={() => setShowEditModal(false)}
              onDelete={() => {
                setShowEditModal(false);
                navigate("/candidates");
              }}
            />
          </div>
        </div>
      )}

      {showCreateApplicationModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <ApplicationForm
              candidate={candidate}
              onSave={() => {
                setShowCreateApplicationModal(false);
                queryClient.invalidateQueries(["candidate-applications", id]);
              }}
              onCancel={() => {
                setShowCreateApplicationModal(false);
              }}
            />
          </div>
        </div>
      )}

      {showCVGenerationModal && (
        <CVGeneration
          candidateId={Number.parseInt(id!)}
          candidateName={`${candidate.first_name} ${candidate.last_name}`}
          onClose={() => setShowCVGenerationModal(false)}
        />
      )}

      {showAssessmentModal && selectedJobForAssessment && (
        <CandidateAssessment
          candidateId={Number.parseInt(id!)}
          jobId={selectedJobForAssessment.id}
          onClose={() => {
            setShowAssessmentModal(false);
            setSelectedJobForAssessment(null);
          }}
        />
      )}

      {showEmailModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowEmailModal(false);
            setSelectedJobForEmail(null);
          }}
        >
          <div
            className="modal large-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedJobForEmail ? (
              <EmailComposer
                candidateId={Number.parseInt(id!)}
                jobId={selectedJobForEmail}
                mode="recruiter"
                onClose={() => {
                  setShowEmailModal(false);
                  setSelectedJobForEmail(null);
                }}
                onSent={() => {
                  queryClient.invalidateQueries(["candidate", id]);
                  setShowEmailModal(false);
                  setSelectedJobForEmail(null);
                }}
              />
            ) : applications && applications.length === 1 ? (
              <EmailComposer
                candidateId={Number.parseInt(id!)}
                jobId={applications[0].job_id}
                mode="recruiter"
                onClose={() => {
                  setShowEmailModal(false);
                  setSelectedJobForEmail(null);
                }}
                onSent={() => {
                  queryClient.invalidateQueries(["candidate", id]);
                  setShowEmailModal(false);
                }}
              />
            ) : applications && applications.length > 1 ? (
              <div className="job-selection-modal">
                <div className="modal-header">
                  <h2>{t("candidates.detail.selectJobToEmail")}</h2>
                  <button
                    className="close-btn"
                    onClick={() => {
                      setShowEmailModal(false);
                      setSelectedJobForEmail(null);
                    }}
                  >
                    x
                  </button>
                </div>
                <div className="job-list">
                  {applications.map((app) => (
                    <div
                      key={app.id}
                      className="job-option-card"
                      onClick={() => {
                        setSelectedJobForEmail(app.job_id);
                      }}
                    >
                      <div className="job-option-header">
                        <h3>
                          {app.job_title || t("candidates.detail.untitledJob")}
                        </h3>
                        {app.status && (
                          <span className="stage-badge">{app.status}</span>
                        )}
                      </div>
                      {app.job_location && (
                        <p className="job-option-location">
                          <MapPin size={14} /> {app.job_location}
                        </p>
                      )}
                      <div className="job-option-footer">
                        <span className="job-option-status">
                          {t("common:status.label")}: {app.status || "active"}
                        </span>
                        <span className="job-option-action">
                          {t("candidates.detail.clickToSelect")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-applications-message">
                <h3>{t("candidates.detail.noApplicationsFound")}</h3>
                <p>{t("candidates.detail.createApplicationFirstForEmail")}</p>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowEmailModal(false);
                    setShowCreateApplicationModal(true);
                  }}
                >
                  {t("candidates.detail.createApplication")}
                </button>
                <button
                  className="btn-secondary"
                  style={{ marginLeft: "10px" }}
                  onClick={() => setShowEmailModal(false)}
                >
                  {t("common:actions.cancel")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Job-Specific CV Generation Modal */}
      {showCVModal && selectedApplicationForCV && (
        <JobSpecificCVGeneration
          applicationId={selectedApplicationForCV.id}
          candidateName={
            candidate
              ? `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim()
              : t("candidates.detail.candidateFallback")
          }
          jobTitle={
            selectedApplicationForCV.job_title ||
            t("candidates.detail.jobFallback")
          }
          onClose={handleCloseCVModal}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={t("candidates.detail.deleteCandidate")}
        message={
          <>
            {t("candidates.detail.deleteConfirmation")}{" "}
            <strong>
              {candidate?.first_name} {candidate?.last_name}
            </strong>
            ?
            <br />
            <br />
            {t("candidates.detail.deleteWarning")}
          </>
        }
        confirmText={t("common:actions.delete")}
        cancelText={t("common:actions.cancel")}
        variant="danger"
        isLoading={isDeleting}
      />
      <PersonnelNotifications
        notification={tcNotification}
        onCloseNotification={tcCloseNotification}
        confirmDialog={tcConfirmDialog}
        onConfirm={tcConfirmRemoval}
        onCancel={tcCancelRemoval}
      />
      <PersonnelNotifications
        notification={recNotification}
        onCloseNotification={recCloseNotification}
        confirmDialog={recConfirmDialog}
        onConfirm={recConfirmRemoval}
        onCancel={recCancelRemoval}
      />
    </div>
  );
};

export default CandidateDetail;
